// What a rejected session does with the Teacher Loop build flag on.
//
// A 401 in service.js or service/api.js (outside an LMS frame) ends the
// session: deleteProfileCookies(), localStorage.clear(), then the tab goes to
// '/'. A browser can hold a jwtToken that is not past its exp but that the
// server rejects (the signing secret rotated, a damaged token), and the app
// shell's first call then 401s. For the Teacher Loop that did two kinds of
// harm: a teacher who tapped a /check/<token>, /check/verify, /thanks/<code>
// or /gift/verify link was bounced to My Drafts or the phone wall, and the
// jeddle.tl.* keys were wiped (a pending claim with its one-time claim ticket,
// a teacher's saved review, a student's sent-message copies).
//
// With the flag on, the 401 branches call endSessionKeepingTeacherLoop instead:
//   - on every route, storage is cleared except the jeddle.tl.* keys;
//   - on a Teacher Loop public page the tab does not go to '/'. The session
//     keys are dropped (as App.jsx does for a provably expired token) and the
//     same URL is reloaded once, so the page boots logged out. It never
//     reloads when no session was stored (nothing would change), twice in one
//     page load (concurrent 401s), or again within SESSION_RELOAD_WINDOW_MS of
//     this tab's last such reload (sessionStorage; best effort, and safe
//     without it because the reload follows dropping the session).
//   - a one-time confirmation (/check/verify, /gift/verify) in flight holds
//     that reload until its answer is stored; one started after the reload
//     was issued is not sent, because its answer would be lost with the page.
//
// The callers test the flag as the literal `process.env.REACT_APP_TEACHER_LOOP
// === 'on'` first, so with the flag off they run exactly the code they ran
// before. This module imports only the dependency-free flag module: it is
// part of the main bundle and carries none of the feature's code.
import { isTeacherLoopStandalonePath } from './teacherLoopGate';

/** Every key the Teacher Loop keeps in localStorage starts with this. */
export const TEACHER_LOOP_STORAGE_PREFIX = 'jeddle.tl.';

/** sessionStorage: when this tab last reloaded for a rejected session (ms). */
export const SESSION_RELOAD_KEY = 'jeddle.tl.sessionReloadAt';

/** A second rejected session this soon after that reload stays put. */
export const SESSION_RELOAD_WINDOW_MS = 2 * 60 * 1000;

/**
 * For this long after the reload is issued, this document counts as about to
 * go: a new one-time confirmation is not started (see withSessionReloadHeld).
 * Bounded, so a reload that never happens (the navigation was stopped or
 * cancelled) cannot leave the confirm button dead.
 */
export const SESSION_RELOAD_PENDING_MS = 10 * 1000;

// What counts as a stored session: the JWT (localStorage-JWT hosts, which are
// staging and production) or the user id (also set on cookie-auth hosts).
const SESSION_KEYS = ['jwtToken', 'userId'];

/**
 * The reload itself, kept on an object so a test can watch it (jsdom cannot
 * reload a page).
 */
export const teacherLoopSessionBrowser = {
  reload: () => window.location.reload(),
};

// Per page load: the reload is decided once, whatever the number of 401s.
let reloadDecided = false;
// A one-time confirmation in flight holds the reload until it settles.
let holds = 0;
let reloadWaiting = false;
// When this document asked the browser to reload (0: never).
let reloadIssuedAt = 0;

export const __resetTeacherLoopSessionForTests = () => {
  reloadDecided = false;
  holds = 0;
  reloadWaiting = false;
  reloadIssuedAt = 0;
};

// The route as App.jsx reads it for publicPaths: the hash route, else the path.
const currentRoute = () => {
  const { hash, pathname } = window.location;
  return hash ? hash.substring(1).split('?')[0] : pathname;
};

/** True on /check/<token>, /check/verify, /thanks/<code> and /gift/verify. */
export const isOnTeacherLoopPublicPage = () => {
  try {
    return isTeacherLoopStandalonePath(currentRoute());
  } catch {
    return false;
  }
};

const hasStoredSession = () => {
  try {
    return SESSION_KEYS.some((key) => Boolean(localStorage.getItem(key)));
  } catch {
    return false;
  }
};

/**
 * localStorage.clear(), except every jeddle.tl.* key stays. Storage that
 * cannot be listed is cleared in full, as before.
 */
export const clearStorageKeepingTeacherLoop = () => {
  let keys = null;
  try {
    if (
      typeof localStorage.length === 'number' &&
      typeof localStorage.key === 'function'
    ) {
      keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (typeof key === 'string') keys.push(key);
      }
    }
  } catch {
    keys = null;
  }
  if (keys === null) {
    try {
      localStorage.clear();
    } catch {
      // nothing more can be done with this storage
    }
    return;
  }
  keys
    .filter((key) => !key.startsWith(TEACHER_LOOP_STORAGE_PREFIX))
    .forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // keep clearing the rest
      }
    });
};

const tabStorage = () => {
  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
};

const reloadedRecently = (now) => {
  const storage = tabStorage();
  if (!storage) return false;
  try {
    const at = Number(storage.getItem(SESSION_RELOAD_KEY));
    return at > 0 && now >= at && now - at < SESSION_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
};

const reloadNow = () => {
  reloadIssuedAt = Date.now();
  try {
    tabStorage()?.setItem(SESSION_RELOAD_KEY, String(reloadIssuedAt));
  } catch {
    // the per-page-load guard and the dropped session still prevent a loop
  }
  teacherLoopSessionBrowser.reload();
};

// The browser was asked to reload this document moments ago. Until that
// navigation commits the old page stays on screen and still takes clicks.
const reloadIssuedRecently = () => {
  if (reloadIssuedAt <= 0) return false;
  const now = Date.now();
  return (
    now >= reloadIssuedAt && now - reloadIssuedAt < SESSION_RELOAD_PENDING_MS
  );
};

/**
 * True while a rejected-session reload is about to replace this document: it
 * waits for a confirmation in flight, or it was issued moments ago. A Teacher
 * Loop page uses it to leave a one-time hand-off in place for the reloaded
 * page instead of losing it with this one.
 */
export const isSessionReloadPending = () =>
  reloadWaiting || reloadIssuedRecently();

const reloadOnce = () => {
  if (reloadDecided) return;
  reloadDecided = true;
  if (reloadedRecently(Date.now())) return;
  if (holds > 0) {
    reloadWaiting = true;
    return;
  }
  reloadNow();
};

/**
 * Ends a session the server rejected (a 401), keeping the Teacher Loop's
 * jeddle.tl.* keys. `deleteSession` is userLocalDetails.deleteProfileCookies.
 *
 * @returns {boolean} true on a Teacher Loop public page: the caller stays (no
 *   redirect; a reload may be on its way). false: the caller goes to '/' as
 *   before.
 */
export const endSessionKeepingTeacherLoop = (deleteSession) => {
  const stay = isOnTeacherLoopPublicPage();
  const hadSession = stay && hasStoredSession();
  deleteSession();
  clearStorageKeepingTeacherLoop();
  if (stay && hadSession) reloadOnce();
  return stay;
};

/**
 * Runs `work` (a one-time confirmation) with any rejected-session reload held
 * until it settles, so the reload cannot cut off its answer. A reload decided
 * meanwhile runs once the last hold is released, after the caller's own
 * follow-up (storing the result) has run.
 *
 * Once the reload has been issued it is too late to hold it: `work` does not
 * run (resolves undefined). Its POST could still reach the server and spend
 * the link, and the answer (the one-time claim ticket) would be lost with
 * this document. The reloaded page offers the button again.
 */
export const withSessionReloadHeld = async (work) => {
  if (reloadIssuedRecently()) return undefined;
  holds += 1;
  try {
    return await work();
  } finally {
    holds -= 1;
    if (holds === 0 && reloadWaiting) {
      reloadWaiting = false;
      setTimeout(reloadNow, 0);
    }
  }
};
